import { useState, useEffect } from "react";
import { cn } from "../../lib/utils";
import type { StatusCounts, ChecklistProgress } from "../../lib/boardUtils";

interface ContextStripProps {
  statusCounts: StatusCounts;
  checklistProgress: ChecklistProgress;
}

const LAUNCH_DATE = new Date("2026-03-04T00:00:00");

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function getWeekLabel(now: Date): string {
  const diffMs = now.getTime() - LAUNCH_DATE.getTime();

  if (diffMs < 0) {
    const remaining = Math.abs(diffMs);
    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    return `W0 · Launch in ${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weekNum = Math.min(4, Math.floor(diffDays / 7) + 1);
  const dayInWeek = (diffDays % 7) + 1;
  const weekNames = ["", "Launch", "Optimize", "Scale", "Convert"];
  return `W${weekNum} · ${weekNames[weekNum]} · Day ${dayInWeek} of 7`;
}

export function ContextStrip({ statusCounts, checklistProgress }: ContextStripProps) {
  const [label, setLabel] = useState(() => getWeekLabel(new Date()));

  useEffect(() => {
    const timer = setInterval(() => {
      setLabel(getWeekLabel(new Date()));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <footer
      className={cn(
        "h-[32px] shrink-0 flex items-center justify-between",
        "px-[var(--page-padding-desktop)]",
        "border-t border-[var(--color-border-stroke)] bg-[var(--color-bg-sidebar)]",
        "text-[14px] text-[var(--color-text-muted)] tabular-nums",
      )}
    >
      <span key={label} className="animate-number-tick">{label}</span>
      <div className="flex items-center gap-[var(--spacing-4)]">
        <span>{statusCounts.total} cards</span>
        <span>·</span>
        <span>{statusCounts.in_progress} active</span>
        <span>·</span>
        <span>{statusCounts.blocked} blocked</span>
        <span>·</span>
        <span>Today {checklistProgress.checked}/{checklistProgress.total}</span>
      </div>
    </footer>
  );
}
